import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { AUFlagIcons, CloseDialogIcon } from "assets/styles/icons";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleUpdateAccount } from "../../introduction/YourDetails";
import { ProfileActions } from "state/profile/Action";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import { Formik } from "formik";
import {
  RegisterSchema,
  YourBusinessReviewSchema,
  YourLoanRNDReviewSchema,
} from "shared/helpers/Schema";
import { ExpandMore } from "@mui/icons-material";
import {
  BROKER_COMMISSION_RATE,
  ENTITY_TYPE,
  TAX_YEAR,
} from "shared/helpers/Constants";
import {
  handleCheckABN,
  handleSaveBusinessRND,
} from "../../finalise/BusinessDetails";
import AccordionReviewComponents from "./AccordionReviewComponents";
import { useNavigate } from "react-router-dom";
import { useProgress } from "context/ProgressProvider";
import { handleSaveLoanRND } from "../../introduction/LoanDetails";
import dayjs from "dayjs";
import { DateCalendar } from "@mui/x-date-pickers";
import Utils from "shared/helpers/Utils";

const AccordionReviewDetails: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>("panel1");
  const [showDialog, setShowDialog] = useState<string>("");
  const [dialogRndDisable, setDialogRnDDisable] = useState("");
  const [existingLoanStatus, setExistingLoanStatus] = useState("Native");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProfile, isBroker } = useProgress();

  const handleChange =
    (panel: string) => (event: React.MouseEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const checkUpdateData = (data) => {
    if (data) {
      if (data.includes("details")) {
        return { title: "your details", name: "details" };
      }

      if (data.includes("loan")) {
        return { title: "your loan", name: "loan" };
      }

      if (data.includes("business")) {
        return { title: "your business", name: "business" };
      }
    }
  };

  const handleSuccessForm = (data) => {
    dispatch(ProfileActions.setRndProfile(data));
    setShowDialog("");
  };

  const handleSubmitForm = (data, formValues, formik, showDialog) => {
    const updateBusiness = (data) => {
      handleSaveBusinessRND(data, currentProfile.loadId, (response) => {
        if (response) {
          handleSuccessForm({
            abn: data.abn,
            registeredEntityName: data.registeredEntityName,
            entityAddress: data.entityAddress,
            entityType: data.entityType,
            legalEntityType: data.legalEntityType,
          });
        }
      });
    };

    if (data) {
      if (data.name === "details") {
        handleUpdateAccount(formValues, formik, (ev) => {
          handleSuccessForm(formValues);
        });
      }
      if (data.name === "loan") {
        handleSaveLoanRND(
          formValues,
          currentProfile.loadId,
          () => {
            handleSuccessForm(formValues);
          },
          null,
          isBroker,
        );
      }
      if (data.name === "business") {
        let data = {
          abn: formValues.abn.toString(),
          entityType: formValues.entityType,
        };
        if (showDialog === "business-3") {
          handleCheckABN(formValues, (ev) => {
            if (ev) {
              updateBusiness({
                ...data,
                registeredEntityName: ev.registeredEntityName,
                entityAddress: `${ev.postcode} ${ev.state}`,
              });
            }
            formik.setSubmitting(false);
          });
          return;
        }
        updateBusiness(data);
      }
    }
  };

  return (
    <React.Fragment>
      {/* Your details */}
      <Dialog
        open={showDialog !== ""}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setShowDialog("");
          setExistingLoanStatus("Native");
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">
              Update {checkUpdateData(showDialog)?.title}
            </Typography>
          </Box>
        </DialogTitle>

        <Formik
          enableReinitialize
          validationSchema={
            checkUpdateData(showDialog)?.name === "details"
              ? RegisterSchema
              : checkUpdateData(showDialog)?.name === "loan"
                ? YourLoanRNDReviewSchema
                : checkUpdateData(showDialog)?.name === "business"
                  ? YourBusinessReviewSchema
                  : ""
          }
          validateOnMount
          onSubmit={(formValues, formik) => {
            formik.setSubmitting(true);
            handleSubmitForm(
              checkUpdateData(showDialog),
              formValues,
              formik,
              showDialog,
            );
          }}
          initialValues={
            checkUpdateData(showDialog)?.name === "details"
              ? {
                  firstName: currentProfile.firstName,
                  lastName: currentProfile.lastName,
                  email: currentProfile.email,
                  phoneNumber: currentProfile.phoneNumber || "+61 ",
                }
              : checkUpdateData(showDialog)?.name === "loan"
                ? {
                    loanAmount: Utils.formatCurrency(currentProfile.loanAmount),
                    annualTurnover: Utils.formatCurrency(
                      currentProfile.annualTurnover,
                    ),
                    hasATOClaims: currentProfile.hasATOClaims,
                    hasTaxIncentive: currentProfile.hasTaxIncentive,
                    taxYear: currentProfile.taxYear,
                    estimatedTaxFilingDate:
                      currentProfile.estimatedTaxFilingDate,
                    estimatedExpenditureAmount: Utils.formatCurrency(
                      currentProfile.estimatedExpenditureAmount,
                    ),
                    brokerCommissionRate: currentProfile.brokerCommissionRate,
                  }
                : checkUpdateData(showDialog)?.name === "business"
                  ? {
                      entityType: currentProfile.entityType,
                      companyAddress: currentProfile.companyAddress,
                      businnessOrCompanyAddress:
                        currentProfile.businnessOrCompanyAddress,
                      abn: currentProfile.abn,
                      auRegDate: currentProfile.auRegDate,
                    }
                  : {}
          }
        >
          {({
            handleSubmit,
            errors,
            isSubmitting,
            values,
            setValues,
            dirty,
          }: any) => {
            console.log(errors);

            if (values?.hasATOClaims === false) {
              setShowDialog("");
              setDialogRnDDisable("lodged");
            }
            if (values?.hasTaxIncentive === false) {
              setShowDialog("");
              setDialogRnDDisable("tax");
            }
            return (
              <>
                <DialogContent>
                  <form
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                  >
                    <>
                      {showDialog === "details-1" && (
                        <>
                          <Box mt={3}>
                            <AppFormikTextField
                              placeholder="First name"
                              label="First name"
                              name="firstName"
                              outerLabel
                              className="ezifin-default-field"
                            />
                          </Box>

                          <Box mt={3}>
                            <AppFormikTextField
                              placeholder="Last name"
                              label="Last name"
                              name="lastName"
                              outerLabel
                              className="ezifin-default-field"
                            />
                          </Box>
                        </>
                      )}
                      {showDialog === "details-2" && (
                        <Box mt={3}>
                          <AppFormikTextField
                            placeholder="Email address"
                            label="Email address"
                            name="email"
                            outerLabel
                            className="ezifin-default-field cursor-not-allowed"
                            inputProps={{ readOnly: true }}
                            blockedAndReadOnly={true}
                          />
                        </Box>
                      )}
                      {showDialog === "details-3" && (
                        <Box mt={3}>
                          <AppFormikTextField
                            placeholder="Phone number"
                            label="Phone number"
                            name="phoneNumber"
                            outerLabel
                            className="ezifin-default-field"
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AUFlagIcons />
                                  </InputAdornment>
                                ),
                              },
                            }}
                            type="customAUPhone"
                          />
                        </Box>
                      )}
                    </>

                    <>
                      {showDialog === "loan-1" && (
                        <AppFormikTextField
                          placeholder="Loan amount"
                          label="Loan amount"
                          name="loanAmount"
                          outerLabel
                          className="ezifin-default-field"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            },
                          }}
                          customIsoDollar
                        />
                      )}
                      {showDialog === "loan-2" && (
                        <AppFormikTextField
                          placeholder="Business’s annual turnover"
                          label="Business’s annual turnover"
                          name="annualTurnover"
                          outerLabel
                          className="ezifin-default-field"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            },
                          }}
                          customIsoDollar
                        />
                      )}

                      {showDialog === "loan-3" && (
                        <AppFormikSelect
                          placeholder="Has ATO claims?"
                          label="Has ATO claims?"
                          name="hasATOClaims"
                          items={[
                            {
                              value: true,
                              label: "Yes",
                            },
                            {
                              value: false,
                              label: "No",
                            },
                          ]}
                          valueKey="value"
                          titleKey="label"
                          outerLabel
                          iconComponent={ExpandMore}
                          className="ezifin-default-field"
                          labelSize={16}
                        />
                      )}

                      {showDialog === "loan-4" && (
                        <AppFormikSelect
                          placeholder="Has tax incentive?"
                          label="Has tax incentive?"
                          name="hasTaxIncentive"
                          items={[
                            {
                              value: true,
                              label: "Yes",
                            },
                            {
                              value: false,
                              label: "No",
                            },
                          ]}
                          valueKey="value"
                          titleKey="label"
                          outerLabel
                          iconComponent={ExpandMore}
                          className="ezifin-default-field"
                          labelSize={16}
                        />
                      )}
                      {showDialog === "loan-5" && (
                        <AppFormikSelect
                          placeholder="Select"
                          label="Tax year"
                          name="taxYear"
                          items={TAX_YEAR}
                          valueKey="value"
                          titleKey="label"
                          outerLabel
                          iconComponent={ExpandMore}
                          className="ezifin-default-field"
                          labelSize={16}
                        />
                      )}
                      {showDialog === "loan-6" && (
                        <DateCalendar
                          disabled={
                            currentProfile.isAllowedToEdit?.reviewDetails ===
                            null
                              ? false
                              : currentProfile.isAllowedToEdit
                                    ?.reviewDetails === undefined
                                ? false
                                : !currentProfile.isAllowedToEdit?.reviewDetails
                          }
                          minDate={dayjs()}
                          maxDate={dayjs().add(391, "day")}
                          onChange={(date) => {
                            const formattedDate =
                              dayjs(date).format("YYYY-MM-DD");
                            setValues({
                              ...values,
                              estimatedTaxFilingDate: formattedDate,
                            });
                          }}
                          slotProps={{
                            day: {
                              sx: {
                                "&.MuiPickersDay-root.Mui-selected": {
                                  backgroundColor: "#2d68f7",
                                  color: "white",
                                },
                              },
                            },
                          }}
                          sx={{
                            "&.MuiDateCalendar-root": {
                              maxWidth: "100%",
                            },
                          }}
                          {...(currentProfile.estimatedTaxFilingDate
                            ? { value: dayjs(values.estimatedTaxFilingDate) }
                            : {})}
                        />
                      )}
                      {showDialog === "loan-7" && (
                        <AppFormikTextField
                          placeholder="Estimated expenditure amount"
                          label="Estimated expenditure amount"
                          name="estimatedExpenditureAmount"
                          outerLabel
                          className="ezifin-default-field"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            },
                          }}
                          customIsoDollar
                        />
                      )}
                      {isBroker && showDialog === "loan-8" && (
                        <AppFormikSelect
                          placeholder="Select"
                          label="Broker Commission Rate"
                          name="brokerCommissionRate"
                          items={BROKER_COMMISSION_RATE}
                          valueKey="value"
                          titleKey="label"
                          outerLabel
                          iconComponent={ExpandMore}
                          className="ezifin-default-field"
                          labelSize={16}
                        />
                      )}
                    </>

                    <>
                      {showDialog === "business-1" && (
                        <AppFormikSelect
                          placeholder="Select"
                          name="entityType"
                          label="Entity type"
                          items={ENTITY_TYPE}
                          valueKey="value"
                          titleKey="label"
                          outerLabel
                          iconComponent={ExpandMore}
                          className="ezifin-default-field"
                          labelSize={16}
                        />
                      )}
                      {showDialog === "business-2" && (
                        <AppFormikTextField
                          placeholder="Legal entity name"
                          label="Entity name"
                          name="legalEntityType"
                          outerLabel
                          className="ezifin-default-field"
                          allowComma
                        />
                      )}
                      {showDialog === "business-3" && (
                        <AppFormikTextField
                          placeholder="Enter ABN number"
                          label="ABN"
                          name="abn"
                          outerLabel
                          className="ezifin-default-field"
                          removeSpacing
                          returnAsString
                        />
                      )}
                      {showDialog === "business-4" && (
                        <AppFormikTextField
                          placeholder="Registered entity name"
                          label="Registered entity name"
                          name="registeredEntityName"
                          outerLabel
                          className="ezifin-default-field"
                        />
                      )}
                      {showDialog === "business-5" && (
                        <AppFormikTextField
                          placeholder="Registered entity address"
                          label="Entity address"
                          name="entityAddress"
                          outerLabel
                          className="ezifin-default-field"
                          allowComma
                        />
                      )}
                    </>
                  </form>
                </DialogContent>
                <DialogActions>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                      paddingTop: "12px",
                    }}
                  >
                    <AppSecondaryButton
                      title="Cancel"
                      className="btn-small"
                      onClick={() => {
                        setExistingLoanStatus("Native");
                        setShowDialog("");
                      }}
                      fullWidth
                    />
                    <AppPrimaryButton
                      title="Update"
                      isLoading={isSubmitting}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={
                        !dirty ||
                        isSubmitting ||
                        Object.keys(errors).length > 0 ||
                        values?.hasATOClaims === false ||
                        values?.hasTaxIncentive === false
                      }
                      fullWidth
                    />
                  </Box>
                </DialogActions>
              </>
            );
          }}
        </Formik>
      </Dialog>

      <AccordionReviewComponents
        expanded={expanded}
        handleChange={handleChange}
        setShowDialog={setShowDialog}
        setExistingLoanStatus={setExistingLoanStatus}
      />

      <Dialog open={dialogRndDisable !== ""} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">
              {dialogRndDisable === "lodged" && "R&D Claim History"}
              {dialogRndDisable === "tax" && "R&D Eligibility Check"}
            </Typography>
            <IconButton
              onClick={() => {
                setDialogRnDDisable("");
              }}
            >
              <CloseDialogIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <>
          <DialogContent>
            <Typography variant="h3" fontWeight={400} mb={3}>
              {dialogRndDisable === "lodged" && isBroker
                ? "To help assess your customer eligibility, please let us know whether your customer have lodged any prior or current R&D claims with the ATO."
                : dialogRndDisable === "lodged" && !isBroker
                  ? "To help assess your eligibility, please let us know whether you’ve lodged any prior or current R&D claims with the ATO."
                  : ""}
              {dialogRndDisable === "tax" && isBroker
                ? "To help assess your customer eligibility, please confirm whether this activity is considered eligible under the ATO’s R&D Tax Incentive."
                : dialogRndDisable === "tax" && !isBroker
                  ? " To help assess your eligibility, please confirm whether this activity is considered eligible under the ATO’s R&D Tax Incentive."
                  : ""}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ gap: "12px" }}>
            <AppPrimaryButton
              title="Cancel"
              className="btn-small"
              onClick={() => {
                setDialogRnDDisable("");
              }}
              fullWidth
            />
          </DialogActions>
        </>
      </Dialog>
    </React.Fragment>
  );
};

export default AccordionReviewDetails;
